
import React, { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import StandardFooter from '@/components/layout/StandardFooter';
import { toast } from 'sonner';
import { useCompanySearch } from '@/hooks/useCompanySearch';
import CompanySearch from '@/components/company-search/CompanySearch';
import CompanyCardsList from '@/components/company-search/CompanyCardsList';
import EmptySearchResults from '@/components/company-search/EmptySearchResults';
import SearchResultsHeader from '@/components/company-search/SearchResultsHeader';

const Reviews = () => {
  const [searchParams] = useSearchParams();
  const isShareFlow = searchParams.get('action') === 'share';
  
  const {
    searchTerm,
    setSearchTerm,
    hasSearched,
    setHasSearched,
    companyCards,
    handleSearch,
    clearSearch,
    isLoading
  } = useCompanySearch();
  
  // Prompt user if coming from share flow
  useEffect(() => {
    if (isShareFlow) {
      setHasSearched(false);
      setTimeout(() => {
        toast.info(
          "Please search for the company you'd like to review",
          { duration: 5000 }
        );
      }, 500);
    }
  }, [isShareFlow, setHasSearched]);

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    if (value === '') {
      setHasSearched(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Centered search input with header above it */}
          <div className={`flex flex-col items-center justify-center ${!hasSearched || companyCards.length === 0 ? 'min-h-[40vh]' : 'mb-10'}`}>
            {isShareFlow && (
              <h1 className="text-3xl font-bold text-redflags-dark mb-8 text-center">
                Find the Company to Review
              </h1>
            )}
            
            {!isShareFlow && (
              <h1 className="text-3xl font-bold text-redflags-dark mb-8 text-center">
                Company Profiles
              </h1>
            )}
            
            <div className="w-full max-w-2xl mb-6">
              <CompanySearch 
                searchTerm={searchTerm}
                onSearchChange={handleSearchTermChange}
                onSearch={handleSearch}
                isShareFlow={isShareFlow}
                isLoading={isLoading}
              />
            </div>
            
            {!hasSearched && (
              <p className="text-redflags-gray text-center max-w-2xl">
                {isShareFlow 
                  ? "Find the company you want to review by entering its name above"
                  : "Can't see the company you're looking for?"}<br />
                {!isShareFlow && (
                  <>
                    New companies can be requested through the{' '}
                    <Link to="/request-company" className="text-redflags-red hover:underline">
                      'Request New Company'
                    </Link>{' '}
                    option.
                  </>
                )}
              </p>
            )}
          </div>
          
          {hasSearched && (
            <>
              {searchTerm && (
                <SearchResultsHeader 
                  searchTerm={searchTerm}
                  resultsCount={companyCards.length}
                  onClearSearch={clearSearch}
                />
              )}
              
              {companyCards.length > 0 ? (
                <CompanyCardsList 
                  companies={companyCards}
                  isShareFlow={isShareFlow}
                />
              ) : (
                <EmptySearchResults 
                  searchTerm={searchTerm}
                  onClearSearch={clearSearch}
                />
              )}
            </>
          )}
        </div>
      </main>
      
      <StandardFooter />
    </div>
  );
};

export default Reviews;
